<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import {
  generateReportURLParams,
  parseReportURLParams,
} from '../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const route = useRoute();
const router = useRouter();

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const businessHoursSelected = ref(false);
// [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion toggle
const excludeWhatsappGroupsSelected = ref(false);
// [/PROSPEK]

const updateURLParams = () => {
  const params = generateReportURLParams({
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
    // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion toggle
    excludeWhatsappGroups: excludeWhatsappGroupsSelected.value,
    // [/PROSPEK]
    range: selectedDateRange.value,
  });

  router.replace({ query: { ...params } });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
    // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion toggle
    excludeWhatsappGroups: excludeWhatsappGroupsSelected.value,
    // [/PROSPEK]
  });
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  emitChange();
};

const onBusinessHoursToggle = () => {
  emitChange();
};

// [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion toggle
const onExcludeWhatsappGroupsToggle = () => {
  emitChange();
};
// [/PROSPEK]

const initializeFromURL = () => {
  const urlParams = parseReportURLParams(route.query);

  // Set the range type first
  if (urlParams.range) {
    selectedDateRange.value = urlParams.range;
  }

  // Restore dates from URL if available
  if (urlParams.from && urlParams.to) {
    customDateRange.value = [
      new Date(urlParams.from * 1000),
      new Date(urlParams.to * 1000),
    ];
  }

  if (urlParams.businessHours) {
    businessHoursSelected.value = urlParams.businessHours;
  }

  // [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion toggle
  if (urlParams.excludeWhatsappGroups) {
    excludeWhatsappGroupsSelected.value = urlParams.excludeWhatsappGroups;
  }
  // [/PROSPEK]
};

onMounted(() => {
  initializeFromURL();
  emitChange();
});
</script>

<template>
  <div
    class="flex flex-col justify-between gap-3 md:flex-row"
    :class="{ 'pointer-events-none opacity-50': disabled }"
  >
    <div class="flex flex-col flex-wrap items-start gap-2 md:flex-row">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
    </div>
    <div class="flex flex-wrap items-center justify-end gap-2">
      <div class="flex items-center">
        <span class="mx-2 text-sm whitespace-nowrap">
          {{ $t('REPORT.BUSINESS_HOURS') }}
        </span>
        <span>
          <ToggleSwitch
            v-model="businessHoursSelected"
            @change="onBusinessHoursToggle"
          />
        </span>
      </div>

      <!-- [PROSPEK] 2026-06-19 Add report-level WhatsApp group exclusion toggle -->
      <div class="flex items-center">
        <span class="mx-2 text-sm whitespace-nowrap">
          {{ $t('REPORT.EXCLUDE_WHATSAPP_GROUPS') }}
        </span>
        <span>
          <ToggleSwitch
            v-model="excludeWhatsappGroupsSelected"
            @change="onExcludeWhatsappGroupsToggle"
          />
        </span>
      </div>
      <!-- [/PROSPEK] -->
    </div>
  </div>
</template>
